import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Radio, MapPin, Wifi, WifiOff, AlertTriangle, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingSpinner } from '@/components/ui/loading';
import { useActiveRole } from '@/features/auth/hooks/use-active-role';
import { formatRelativeTime } from '@/lib/utils';
import { UserRole } from '@/types/api';
import type { Gateway, GatewayStatus } from '@/types/api';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { getPropertyGateways } from '@/features/gateways/api/gateways-api';
import { GatewayLocationDialog } from '@/features/gateways/components/gateway-location-dialog';
import { AssignGatewayModal } from './assign-gateway-modal';

const statusBadge: Record<GatewayStatus, 'warning' | 'success' | 'default' | 'error'> = {
  Provisioning: 'warning',
  Active: 'success',
  Inactive: 'default',
  Error: 'error',
};

interface PropertyGatewaysTabProps {
  propertyId: string;
  onGatewayAssigned?: () => void;
}

export function PropertyGatewaysTab({ propertyId, onGatewayAssigned }: PropertyGatewaysTabProps) {
  const navigate = useNavigate();
  const activeRole = useActiveRole();
  const canEdit = activeRole === UserRole.Owner || activeRole === UserRole.Admin || activeRole === UserRole.Manager;

  // Single atomic state — avoids the subtle case where setGateways(items) and
  // setHasLoaded(true) land in separate microtask-driven renders, which React
  // 18 auto-batching doesn't merge across promise callback boundaries. With
  // one state variable there's no intermediate render where "loaded" and
  // "empty" can be simultaneously true for one frame.
  type State = { status: 'loading' } | { status: 'ready'; gateways: Gateway[] };
  const [state, setState] = useState<State>({ status: 'loading' });
  const [editingGateway, setEditingGateway] = useState<Gateway | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    getPropertyGateways(propertyId)
      .then((items) => { if (!cancelled) setState({ status: 'ready', gateways: items }); })
      .catch(() => { if (!cancelled) setState({ status: 'ready', gateways: [] }); });
    return () => { cancelled = true; };
  }, [propertyId]);

  const gateways = state.status === 'ready' ? state.gateways : [];

  function handleAssigned() {
    setAssignOpen(false);
    getPropertyGateways(propertyId)
      .then((items) => setState({ status: 'ready', gateways: items }))
      .catch(() => { /* keep last list on refetch failure */ });
    onGatewayAssigned?.();
  }

  return (
    <div className="flex-1 flex flex-col gap-4">
      {state.status === 'loading' ? (
        <Card variant="elevated" className="flex-1 flex items-center justify-center min-h-[240px]">
          <LoadingSpinner size="lg" />
        </Card>
      ) : gateways.length === 0 ? (
        <Card variant="elevated" className="flex-1 flex items-center justify-center">
          <EmptyState
            icon={<Radio className="w-12 h-12" />}
            title="No gateway assigned yet"
            description="Devices will associate a gateway automatically when they uplink, or you can assign one now."
            action={canEdit ? {
              label: 'Assign Gateway',
              onClick: () => setAssignOpen(true),
              icon: <Plus className="w-4 h-4" />,
            } : undefined}
          />
        </Card>
      ) : (
        <>
          {canEdit && (
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={() => setAssignOpen(true)}>
                <Plus className="w-4 h-4" />
                Assign Gateway
              </Button>
            </div>
          )}
          <Card variant="elevated" padding="none">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Gateway</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Last Seen</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {gateways.map((gw) => (
                  <TableRow
                    key={gw.id}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    onClick={() => navigate(`/gateways/${gw.id}`)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {gw.status === 'Error' ? (
                          <AlertTriangle className="w-4 h-4 text-error-500" />
                        ) : gw.lastSeenAt ? (
                          <Wifi className="w-4 h-4 text-success-500" />
                        ) : (
                          <WifiOff className="w-4 h-4 text-gray-400" />
                        )}
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">{gw.name}</p>
                          <code className="text-xs text-gray-500 dark:text-gray-400 font-mono">{gw.gatewayEui}</code>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusBadge[gw.status]} size="sm" dot>{gw.status}</Badge>
                    </TableCell>
                    <TableCell>
                      {gw.latitude != null && gw.longitude != null ? (
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          <MapPin className="inline w-3.5 h-3.5 mr-1 text-gray-400" />
                          {gw.latitude.toFixed(5)}, {gw.longitude.toFixed(5)}
                        </span>
                      ) : canEdit ? (
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setEditingGateway(gw); }}>
                          <MapPin className="w-4 h-4" />
                          Set location
                        </Button>
                      ) : (
                        <span className="text-sm text-gray-400 italic">Not set</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {gw.lastSeenAt ? formatRelativeTime(gw.lastSeenAt) : 'Never'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        {canEdit && gw.latitude != null && gw.longitude != null && (
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setEditingGateway(gw); }} title="Edit location">
                            <MapPin className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      {editingGateway && (
        <GatewayLocationDialog
          gateway={editingGateway}
          onClose={() => setEditingGateway(null)}
          onSaved={(updated) => {
            setState((prev) =>
              prev.status === 'ready'
                ? {
                    status: 'ready',
                    gateways: prev.gateways.map((g) =>
                      g.id === updated.id
                        ? { ...g, latitude: updated.latitude, longitude: updated.longitude }
                        : g,
                    ),
                  }
                : prev,
            );
            setEditingGateway(null);
          }}
        />
      )}

      {assignOpen && (
        <AssignGatewayModal
          propertyId={propertyId}
          onClose={() => setAssignOpen(false)}
          onAssigned={handleAssigned}
        />
      )}
    </div>
  );
}
